import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { cloneDeep } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import {
    createSiteCardAsync,
    createSiteCardFromFeatureAnalysisAsync
} from '../../../../../redux/site_card/Actions';
import { setHighlightedFeatures } from '../../../../../redux/highlights/Actions';
import {
    addDrawFeature,
    changeDrawMode,
    deleteDrawFeature
} from '../../../../../redux/map/Actions';
import { MAIN_MAP } from '../../../../../redux/map/Constants';

import { makeStyles } from '@material-ui/styles';
import { Box, Button, CircularProgress, MenuItem } from '@material-ui/core';

import PanelContent from '../../common/PanelContent';
import PanelActions from '../../common/PanelActions';
import TextField from '../../../../Common/UI/TextField';
import SelectTextField from '../../../../Common/UI/SelectTextField';

const propTypes = {
    featureConfig: PropTypes.oneOfType([
        PropTypes.shape({
            layerId: PropTypes.number.isRequired,
            featureId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired
        }),
        PropTypes.shape({
            layerId: PropTypes.number.isRequired,
            featureIds: PropTypes.arrayOf(
                PropTypes.oneOfType([PropTypes.string, PropTypes.number])
            ).isRequired
        })
    ]).isRequired,
    defaultName: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
    featureAnalysisId: PropTypes.number
};

const defaultProps = {
    featureConfig: null,
    featureAnalysisId: null
};

const useStyles = makeStyles(theme => styles(theme));

function SiteCardCreator({
    featureConfig,
    defaultName,
    featureAnalysisId,
    onClose
}) {
    const classes = useStyles();
    const currentProject = useSelector(state => state.project.currentProject);
    const projectStructure = currentProject.structure;

    const [columnId, setColumnId] = useState(null);

    const dispatch = useDispatch();
    const dispatchCreateSiteCard = (projectId, name, columnId, params) =>
        dispatch(createSiteCardAsync(projectId, name, columnId, params));
    const dispatchCreateSiteCardFromFeatureAnalysis = (
        featureAnalysisId,
        name,
        columnId,
        params
    ) =>
        dispatch(
            createSiteCardFromFeatureAnalysisAsync(featureAnalysisId, name, columnId, params)
        );

    

    const dispatchSetHighlightedFeatures = features =>
        dispatch(setHighlightedFeatures(features));
    const dispatchAddDrawFeature = feature =>
        dispatch(addDrawFeature(cloneDeep(feature), MAIN_MAP));
    const dispatchDeleteDrawFeature = ids =>
        dispatch(deleteDrawFeature(ids, MAIN_MAP));
    const dispatchChangeDrawMode = (mode, options) =>
        dispatch(changeDrawMode(MAIN_MAP, mode, options));

    const layerId = featureConfig.layerId;
   
    const currentProjectId = currentProject?.id;    
    const analysisProfiles = useSelector(state => {
        const profileIds =
            state.user.features['analysis']?.layers?.find(l => l.layerId === layerId)
                ?.profiles || [];
        return state.lookups.uiData.analysisProfiles.filter(ap =>
            profileIds.includes(ap.id)
        );
    });

    const defaultAnalysisProfile = useSelector(state => {
        const defaultProfiles = state?.user?.prefs?.defaultAnalysisProfiles ?? [];
        const profile = defaultProfiles.find(
            profile => profile.layerId === layerId
        );

        if (profile) return profile.profileId;

        return analysisProfiles.length > 0 ? analysisProfiles[0].id : undefined;
    });

    const siteCardFeature = useSelector(state =>
        state[MAIN_MAP].drawnFeatures.features.find(f => f.properties.siteCardEditor)
    );
    const isSiteCardFeatureSelected = useSelector(state => {
        const drawingFeature = state[MAIN_MAP].drawingFeature;
        return Boolean(
            drawingFeature &&
                siteCardFeature &&
                drawingFeature.id === siteCardFeature.id
        );
    });
    const layer = useSelector(state =>
        state.layers.layers.find(l => l.id === featureConfig.layerId)
    );
    const drawModule = useSelector(state => state[MAIN_MAP].drawModule);

    const [name, setName] = useState(defaultName);
    const [description, setDescription] = useState('');
    const [analysis, setAnalysis] = useState(defaultAnalysisProfile);
    const [hasLoadedGeometry, setHasLoadedGeometry] = useState(false);
    

    useEffect(() => {
        editGeometry();
    }, []);

    useEffect(() => {
        if (siteCardFeature && drawModule.getMode() !== 'locked_direct_select') {
            dispatchChangeDrawMode('locked_direct_select', {
                featureId: siteCardFeature.id
            });
        }
        // This has loaded geometry should be improved as not guaranteed to always work (race conditions)
        if (!isSiteCardFeatureSelected && hasLoadedGeometry) {
            handleClose();
        }
    }, [siteCardFeature, isSiteCardFeatureSelected]);

    useEffect(() => {
        const firstColumn = projectStructure.columns[projectStructure.columnOrder[0]];
        setColumnId(firstColumn?.id);
    }, [currentProject]);

    function createSiteCard() {
        // If a feature analysis has been included then creating the site card from a feature analysis
        if (featureAnalysisId !== null) {
            dispatchCreateSiteCardFromFeatureAnalysis(featureAnalysisId, name, columnId, {
                description: description
            });
        } else if (featureConfig !== null) {
            dispatchCreateSiteCard(currentProjectId, name, columnId,{
                description: description,
                weightingProfileId: analysis,
                ...featureConfig,
                geometry: siteCardFeature.geometry
            });
        }
        handleClose();
    }


    
    useEffect(() => {
        const firstColumn = projectStructure.columns[projectStructure.columnOrder[0]];
        setColumnId(firstColumn?.id);
    }, [currentProject]);

    function editGeometry() {
        axios
            .get(`/api/layers/${featureConfig.layerId}/geometry`, {
                params: {
                    feature_ids: featureConfig.featureIds
                }
            })
            .then(response => {
                // Can only apply properties directly through a feature not multipolygon
                const feature = {
                    geometry: response.data,
                    type: 'Feature',
                    properties: { siteCardEditor: true }
                };
                dispatchAddDrawFeature(feature);
                setHasLoadedGeometry(true);
            })
            .catch(error => {
                if (axios.isCancel(error)) {
                    console.log('cancel');
                } else {
                    console.error(error);
                }
            });
        dispatchSetHighlightedFeatures([]);
    }

    function handleClose() {
        if (siteCardFeature) {
            dispatchDeleteDrawFeature(siteCardFeature.id);
        }
        dispatchChangeDrawMode('simple_select');
        onClose();
    }

    if (!siteCardFeature) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={356}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <PanelContent>
                <TextField
                    name="name"
                    autoFocus
                    fullWidth
                    label="Site card name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                />
                <TextField
                    name="description"
                    fullWidth
                    label="Site card description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    multiline
                    rows={4}
                />
                {projectStructure.columnOrder.length > 1 && (
                            <SelectTextField
                                fullWidth
                                label="List"
                                tooltip="You can select any of your lists in your workspace to save the site card to."
                                value={columnId}
                                onChange={e => setColumnId(e.target.value)}>
                                {projectStructure.columnOrder.map(column => (
                                    <MenuItem
                                        key={projectStructure.columns[column].id}
                                        value={projectStructure.columns[column].id}>
                                        {projectStructure.columns[column].title}
                                    </MenuItem>
                                ))}
                            </SelectTextField>
                        )}
                {featureAnalysisId === null && (
                    <SelectTextField
                        fullWidth
                        label="Analysis Profile"
                        value={analysis}
                        onChange={e => setAnalysis(e.target.value)}>
                        {analysisProfiles.map(profile => (
                            <MenuItem key={profile.id} value={profile.id}>
                                {profile.name}
                            </MenuItem>
                        ))}
                    </SelectTextField>
                )}
                {projectStructure.columnOrder.length > 1 && (
                    <SelectTextField
                        fullWidth
                        label="List"
                        tooltip="You can select any of your lists in your workspace to save the site card to."
                        value={columnId}
                        onChange={e => setColumnId(e.target.value)}>
                        {projectStructure.columnOrder.map(column => (
                            <MenuItem
                                key={projectStructure.columns[column].id}
                                value={projectStructure.columns[column].id}>
                                {projectStructure.columns[column].title}
                            </MenuItem>
                        ))}
                    </SelectTextField>
                )}
            </PanelContent>
            <div className={classes.border} />
            <PanelActions>
                <div className={classes.actionsRoot}>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={false}
                        onClick={createSiteCard}>
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        color="secondary"
                        disabled={false}
                        onClick={handleClose}>
                        Cancel
                    </Button>
                </div>
            </PanelActions>
        </>
    );
}

const styles = theme => ({
    root: {
        padding: theme.constants.spacing.xl,
        '&> *:not(:last-child)': {
            marginBottom: theme.constants.verticalMargin
        }
    },
    border: {
        height: '2px',
        backgroundColor: theme.palette.background.border
    },
    actionsRoot: {
        display: 'flex',
        '& > *': {
            flex: '1 1 0',
            '&:not(:last-child)': {
                marginRight: 12
            }
        }
    }
});

SiteCardCreator.propTypes = propTypes;
SiteCardCreator.defaultProps = defaultProps;

export default SiteCardCreator;
